import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Box, LinearProgress, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography } from '@mui/material';
import { tabsClasses } from '@mui/material/Tabs';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import _ from 'lodash';
import deepmerge from 'deepmerge';

import ProductFilters from '../common/product_filters';
import useNotification from '../../../../core/hooks/use_notification';
import { getProductsForPromotionAction, getProductsForPromotionState } from '../../../../features/product/get_products_for_promotion';
import { getConstantsAction, getConstantsState, invalidateConstants } from '../../../../features/product/get_constants';
import { getAllFoodPartnersAction, getAllFoodPartnersState } from '../../../../features/food_partner/get_all_food_partners';
import { getAllFoodPartnersNotificationData, getConstantsNotificationData, getProductsForPromotionNotificationData } from '../../../../core/utils/notification_utils';
import { GET_ROUTE_PRODUCT_PROMOTIONS_DETAILS } from '../../../../core/constants';
import PaginationControls from '../../../common/pagination_controls';
import { getDefaultProductPaginationQueryValues } from '../../../../core/utils/api_utils';
import PageHeader from '../../../common/page_header';
import BlockIcon from "@mui/icons-material/Block";

const initialQueryParams = {
    ...getDefaultProductPaginationQueryValues,
    query: '',
};

const ProductPromotionsPage = () => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();
    const navigate = useNavigate();
    const location = useLocation();

    const getProductsForPromotion = useSelector(getProductsForPromotionState);
    const getConstants = useSelector(getConstantsState);
    const getAllFoodPartners = useSelector(getAllFoodPartnersState);

    const [tab, setTab] = useState(location?.state?.tab || 0);
    const [queryParams, setQueryParams] = useState(location?.state?.queryParams || initialQueryParams);

    useEffect(() => {
        dispatch(getConstantsAction())
            .then(response => showErrorMessage(getConstantsNotificationData(response)));
        dispatch(getAllFoodPartnersAction())
            .then(response => showErrorMessage(getAllFoodPartnersNotificationData(response)));

        return () => {
            dispatch(invalidateConstants());
        };
    }, [dispatch, showErrorMessage]);

    useEffect(() => {
        const params = {
            ..._.omit(queryParams, [
                queryParams?.foodTypeId === 'none' && 'foodTypeId',
                queryParams?.foodPartnerId === 'none' && 'foodPartnerId',
            ])
        };

        dispatch(getProductsForPromotionAction(params))
            .then(response => showErrorMessage(getProductsForPromotionNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    const onTabChange = (tabValue) => {
        navigate(location.pathname, {});
        setTab(tabValue);
    };

    const renderListView = () => {
        return (
            <Box sx={ { display: 'flex', flexDirection: 'column', gap: 3 } }>
                {
                    (getConstants?.data && getAllFoodPartners?.data) &&
                    <Box>
                        <ProductFilters
                            isProductPromotionList
                            queryParams={ queryParams }
                            setQueryParams={ setQueryParams }
                        />
                    </Box>
                }

                { getProductsForPromotion?.loading && <LinearProgress/> }

                {
                    getProductsForPromotion?.data?.content &&
                    <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
                        <TableContainer component={ Paper } variant="outlined">
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                        <TableCell>Nazwa produktu</TableCell>
                                        <TableCell align="right" sx={ { pr: 2 } }></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        getProductsForPromotion?.data?.content?.map(product => (
                                            <TableRow
                                                key={ product?.id }
                                                hover
                                                sx={ { cursor: 'pointer' } }
                                                onClick={ () => navigate(GET_ROUTE_PRODUCT_PROMOTIONS_DETAILS(product?.id), {
                                                    state: {
                                                        queryParams,
                                                        tab
                                                    }
                                                }) }
                                            >
                                                <TableCell>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 500,
                                                                color: product?.active === false ? 'text.secondary' : 'text.primary'
                                                            }}
                                                        >
                                                            { product?.name }
                                                        </Typography>

                                                        { product?.active === false && (
                                                            <Tooltip title="Produkt jest nieaktywny" placement="top">
                                                                <BlockIcon fontSize="small" color="disabled" />
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="right" sx={ { pr: 2, color: 'text.secondary' } }>
                                                    <ChevronRightIcon fontSize="small"/>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={ { display: 'flex', justifyContent: 'center' } }>
                            <PaginationControls
                                currentPage={ getProductsForPromotion?.data?.pageable?.pageNumber }
                                totalPages={ getProductsForPromotion?.data?.totalPages }
                                setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                            />
                        </Box>
                    </Box>
                }
            </Box>
        );
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3, mb: 4 } }>
            <PageHeader title="Promocje na towar"/>

            <Box sx={ { borderBottom: 1, borderColor: 'divider' } }>
                <Tabs
                    value={ tab }
                    onChange={ (_, value) => onTabChange(value) }
                    sx={ {
                        [`& .${ tabsClasses.scrollButtons }`]: {
                            '&.Mui-disabled': { opacity: 0.3 },
                        },
                    } }
                >
                    <Tab label="Promocje"/>
                </Tabs>
            </Box>

            { tab === 0 && renderListView() }
        </Box>
    );
};

export default ProductPromotionsPage;